// PracticeMCQOthers.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import '../styles/MCQPractice.css';
import { useSelector } from 'react-redux';
import Loader from '../../../components/utils/Loader';
import Tooltip from '../../../components/utils/Tooltip';

const PracticeMCQOthers = () => {
    const authState = useSelector(state => state.authReducer);
    const [fetchData, { loading }] = useFetch();
    const navigate = useNavigate();

    const [selectedSetId, setSelectedSetId] = useState('');
    const [selectedPracticeSetId, setSelectedPracticeSetId] = useState('');

    const [searchText, setSearchText] = useState("");
    const [examFilter, setExamFilter] = useState("");
    const [practiceFilter, setPracticeFilter] = useState("");

    const [appliedFilters, setAppliedFilters] = useState({
        searchText: "",
        examFilter: "",
        practiceFilter: ""
    });

    const [setMasterList, setSetMasterList] = useState([]);
    const [practicedSetList, setPracticedSetList] = useState([]);
    const [practicedMaster, setPracticedMaster] = useState([]);

    // ==================== FETCH DATA ====================
    const fetchSetMasters = useCallback(() => {
        const config = {
            url: "/setmaster/sets",
            method: "post",
            headers: { Authorization: authState.token },
            data: { "isT5w": 0, "setType": "Practice", "setQuestionType": "MCQ" },
        };

        fetchData(config, { showSuccessToast: false })
            .then(response => {
                if (response && Array.isArray(response.setmasterlist)) {
                    setSetMasterList(response.setmasterlist);
                }
            })
            .catch(console.error);
    }, [authState.token, fetchData]);

    const fetchPracticedSetList = useCallback(() => {
        const config = {
            url: "/mypracticedsets",
            method: "get",
            headers: { Authorization: authState.token, "isT5w": 0, "setQuestionType": "MCQ" }
        };

        fetchData(config, { showSuccessToast: false })
            .then(response => {
                if (response && Array.isArray(response.practicedsetlist)) {
                    setPracticedSetList(response.practicedsetlist);
                }
            })
            .catch(console.error);
    }, [authState.token, fetchData]);

    useEffect(() => {
        if (authState.isLoggedIn) fetchSetMasters();
    }, [authState.isLoggedIn, fetchSetMasters]);

    useEffect(() => {
        fetchPracticedSetList();
    }, [fetchPracticedSetList]);

    // ==================== COMPUTE ANALYTICS PER SET ====================
    const computeConfidenceAnalytics = (answersRaw = []) => {
        const answers = answersRaw.map(a => ({
            ...a,
            userAnswer: a.userMCQAnswer,
            correctAnswer: a.correctAnswer,
            confidence: (a.confidence || "sure").toLowerCase()
        }));

        const totalQuestions = answers.length;

        const getStats = (type) => {
            const filtered = answers.filter(a => a.confidence === type);
            const total = filtered.length;
            const correct = filtered.filter(a => a.marksObtained > 0).length;
            const incorrect = total - correct;

            return {
                total,
                correct,
                incorrect,
                percent: totalQuestions ? ((total / totalQuestions) * 100).toFixed(1) : "0.0",
            };
        };

        const sureStats = getStats("sure");
        const doubtfulStats = getStats("doubtful");
        const guessStats = getStats("guess");

        const totalConfidence = sureStats.total + doubtfulStats.total + guessStats.total || 1;

        const sureDeg = (sureStats.total / totalConfidence) * 360;
        const doubtfulDeg = (doubtfulStats.total / totalConfidence) * 360;

        // Quadrant Analysis
        const quadrantStats = { mastery: 0, overconfidence: 0, underconfidence: 0, weak: 0 };

        answers.forEach((a) => {
            const isCorrect = a.marksObtained > 0;
            const conf = a.confidence;

            if (conf === "sure" && isCorrect) quadrantStats.mastery++;
            else if (conf === "sure" && !isCorrect) quadrantStats.overconfidence++;
            else if ((conf === "doubtful" || conf === "guess") && isCorrect)
                quadrantStats.underconfidence++;
            else quadrantStats.weak++;
        });

        const calcAccuracy = (correct, total) =>
            total ? ((correct / total) * 100).toFixed(1) : "0.0";

        return {
            answers,
            sureStats,
            doubtfulStats,
            guessStats,
            sureDeg,
            doubtfulDeg,
            quadrantStats,
            sureAccuracy: calcAccuracy(sureStats.correct, sureStats.total),
            doubtfulAccuracy: calcAccuracy(doubtfulStats.correct, doubtfulStats.total),
            guessAccuracy: calcAccuracy(guessStats.correct, guessStats.total),
        };
    };

    const enrichedPracticedSetList = practicedSetList
        .sort((a, b) => new Date(b.datePracticed) - new Date(a.datePracticed))
        .map(ps => ({
            ...ps,
            ...computeConfidenceAnalytics(ps.answers || [])
        }));

    const sortedSetMasterList = [...setMasterList].sort((a, b) => a.setDisplaySn - b.setDisplaySn);

    const filteredSetMasterList = sortedSetMasterList.filter(setMaster => {
        const matchesSearch =
            setMaster.setTitle.toLowerCase().includes(appliedFilters.searchText.toLowerCase()) ||
            (setMaster.setDesc || "").toLowerCase().includes(appliedFilters.searchText.toLowerCase());

        const matchesExam = appliedFilters.examFilter
            ? setMaster.examTitle === appliedFilters.examFilter
            : true;

        const isPracticed = practicedMaster.some(p => p._id === setMaster._id);

        const matchesPractice =
            appliedFilters.practiceFilter === "practiced" ? isPracticed :
            appliedFilters.practiceFilter === "unpracticed" ? !isPracticed : true;

        return matchesSearch && matchesExam && matchesPractice;
    });

    useEffect(() => {
        const newPracticedMaster = practicedSetList
            .map(ps => sortedSetMasterList.find(sm => sm._id === ps.setId))
            .filter(Boolean);
        setPracticedMaster(newPracticedMaster);
    }, [practicedSetList, sortedSetMasterList]);

    const handleShow = () => {
        setAppliedFilters({ searchText, examFilter, practiceFilter });
    };

    const handleSetMasterView = (setId) => {
        setSelectedSetId(prev => (prev === setId ? null : setId));
    };

    const handlePracticeSetView = (setId) => {
        setSelectedPracticeSetId(prev => (prev === setId ? null : setId));
    };

    const handlePracticeClick = (setId, setTitle, seriesId, seriesTitle, examId, examTitle, examType, timeLimitInMin, isRepeated) => {
        navigate('/mcq-test', {
            state: {
                isT5w: 0,
                setId,
                setTitle,
                seriesId,
                seriesTitle,
                examId,
                examTitle,
                examType,
                timeLimitInMin,
                isRepeatPractice: isRepeated
            }
        });
    };

    const convertTimeLimit = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}:${mins} (Hr:Min)`;
    };

    const convertTimeTaken = (milliseconds) => {
        const hours = Math.floor(milliseconds / 3600000);
        const minutes = Math.floor((milliseconds % 3600000) / 60000);
        const seconds = Math.floor((milliseconds % 60000) / 1000);
        const ms = milliseconds % 1000;
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')} (Hr:Min:Sec)`;
    };

    const formatDateTime = (date) => {
        const d = new Date(date);
        return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()} ` +
               `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
    };

    return (
        <div className="min-h-screen bg-black text-white py-2">
            <div className='border-b border-blue-400 mb-2'>
                <h2 className="text-lg lg:text-xl font-extrabold text-center text-yellow-200 p-2">
                    📖 MCQ Type Questions Practice
                </h2>
            </div>

            <div className="min-h-[85vh] py-2 bg-black">
                <div className="list-container p-4">
                    {loading ? <Loader /> : (
                        <div className="practicelist-container ml-4">
                            <h2 className="text-white bg-blue-600 rounded p-2 mb-2">
                                {setMasterList.length} MCQ Practice Set List
                            </h2>

                            {/* Filters */}
                            <div className="flex flex-col lg:flex-row text-xs lg:text-sm gap-2 mb-4">
                                <input
                                    type="text"
                                    placeholder="Search by title or description..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className="px-3 py-1 text-black rounded w-full lg:w-1/3"
                                />

                                <select value={examFilter} onChange={(e) => setExamFilter(e.target.value)}
                                    className="px-3 py-1 text-black rounded lg:h-12">
                                    <option value="">All Exams</option>
                                    {[...new Set(sortedSetMasterList.map(s => s.examTitle))].map((exam, i) => (
                                        <option key={i} value={exam}>{exam}</option>
                                    ))}
                                </select>

                                <select value={practiceFilter} onChange={(e) => setPracticeFilter(e.target.value)}
                                    className="px-3 py-1 text-black rounded lg:h-12">
                                    <option value="">All</option>
                                    <option value="practiced">Practiced</option>
                                    <option value="unpracticed">Unpracticed</option>
                                </select>

                                <button onClick={handleShow}
                                    className="text-xs lg:text-sm w-20 lg:w-32 bg-yellow-500 text-black px-4 py-1 rounded font-medium hover:bg-yellow-400">
                                    Show
                                </button>
                            </div>

                            <h2 className="text-cyan-200 rounded p-2 mt-2 mb-2 text-xs lg:text-sm">
                                {filteredSetMasterList.length} Sets found
                            </h2>

                            {/* Set Master List */}
                            {filteredSetMasterList.map((setMaster, index) => (
                                <div key={setMaster._id} className='bg-black border border-gray-400 my-4 p-4 rounded-md shadow-md'>
                                    <div className='flex flex-row justify-between p-1'>
                                        <div className='mt-2 text-left'>
                                            <span className={`text-xs lg:text-sm text-cyan-200 font-medium p-1 ${practicedMaster.some(p => p._id === setMaster._id) ? "text-yellow-200" : ""}`}>
                                                {index + 1}. {setMaster.setTitle}
                                            </span>
                                        </div>

                                        <div className='flex flex-row gap-2 place-items-center'>
                                            <Tooltip text="View details" position="top">
                                                <span className='px-2 py-1 text-blue-500 cursor-pointer'
                                                    onClick={() => handleSetMasterView(setMaster._id)}>
                                                    <i className="fa-solid fa-eye"></i>
                                                </span>
                                            </Tooltip>

                                            <button
                                                className={`text-xs lg:text-sm w-32 lg:w-48 mt-2 px-2 py-1 text-white rounded ${practicedMaster.some(p => p._id === setMaster._id) ? 'bg-blue-600' : 'bg-green-600'}`}
                                                onClick={() => handlePracticeClick(
                                                    setMaster._id, setMaster.setTitle, setMaster.seriesId, setMaster.seriesTitle,
                                                    setMaster.examId, setMaster.examTitle, setMaster.examType,
                                                    setMaster.timeLimitInMin,
                                                    practicedMaster.some(p => p._id === setMaster._id) ? 1 : 0
                                                )}
                                            >
                                                {practicedMaster.some(p => p._id === setMaster._id) ? "Repeat Practice" : "Practice Now"}
                                            </button>
                                        </div>
                                    </div>

                                    <div className='text-left text-xs lg:text-sm mt-2 text-gray-200'>
                                        {setMaster.setDesc}
                                    </div>

                                    {selectedSetId === setMaster._id && (
                                        <div className='bg-black text-gray-100 [&_p]:text-gray-100 text-xs lg:text-sm text-left border border-gray-600 p-2 mt-2'>
                                            <p className='text-xs lg:text-sm'><strong>Total Questions:</strong> {setMaster.setTotalQuestions}</p>
                                            <p className='text-xs lg:text-sm'><strong>Time Limit:</strong> {setMaster.timeLimitInMin}</p>
                                            <p className='text-xs lg:text-sm'><strong>Correct Points:</strong> {setMaster.correctAnswerPoints}</p>
                                            <p className='text-xs lg:text-sm'><strong>Wrong Points:</strong> {setMaster.WrongAnswerPoints}</p>
                                            <p className='text-xs lg:text-sm'><strong>Reference Book:</strong> {setMaster.setRefBookName}</p>
                                            <p className='text-xs lg:text-sm'><strong>Author:</strong> {setMaster.setAuthor}</p>
                                            <p className='text-xs lg:text-sm'><strong>Test Series:</strong> {setMaster.seriesTitle}</p>
                                            <p className='text-xs lg:text-sm'><strong>Target Exam:</strong> {setMaster.examTitle}</p>
                                            <p className='text-xs lg:text-sm'><strong>Exam Type:</strong> {setMaster.examType}</p>

                                            <p className='text-xs lg:text-sm'><strong>Institution Name:</strong> {setMaster.examInstitution}</p>
                                            <p className='text-xs lg:text-sm'><strong>Branch Name:</strong> {setMaster.examBranch}</p>
                                            <p className='text-xs lg:text-sm'><strong>Batch/Class Name:</strong> {setMaster.examBatch}</p>
                                            <p className='text-xs lg:text-sm'><strong>Section Name:</strong> {setMaster.examSubBatch}</p>
                                            <p className='text-xs lg:text-sm'><strong>Teacher Code:</strong> {setMaster.examTeacher}</p>
                                            <p className='text-xs lg:text-sm'><strong>Group Code:</strong> {setMaster.examGroup}</p>
                                            <p className='text-xs lg:text-sm'><strong>Student Code:</strong> {setMaster.examStudent}</p>                                                  
                                            <p className='text-xs lg:text-sm'><strong>Session Name:</strong> {setMaster.examSession}</p>                                                  
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* ==================== MY PRACTICED LIST ==================== */}
                            <h2 className="px-2 text-sm lg:text-lg text-gray-200 bg-green-600 rounded p-2 mt-8">
                                My Practiced List
                            </h2>

                            {enrichedPracticedSetList.length === 0 ? (
                                <div className='mt-4 text-lg lg:text-xl text-orange-600'>No records found!</div>
                            ) : (
                                enrichedPracticedSetList.map((practicedSet, index) => (
                                    <div key={practicedSet._id} className='mb-1 bg-black text-white rounded-md shadow-md'>
                                        <div className='flex bg-gray-600 p-2 rounded-sm'>
                                            <span className='font-medium text-xs lg:text-sm'>
                                                {index + 1}. {practicedSet.setTitle}, Date: {formatDateTime(practicedSet.datePracticed)}
                                            </span>
                                            <Tooltip text="View details" position="top">
                                                <span className='ml-auto mr-4 text-blue-500 cursor-pointer text-xs lg:text-sm'
                                                    onClick={() => handlePracticeSetView(practicedSet._id)}>
                                                    <i className="fa-solid fa-eye"></i>
                                                </span>
                                            </Tooltip>
                                        </div>

                                        {selectedPracticeSetId === practicedSet._id && (
                                            <div className='flex flex-col text-left bg-gray-600 text-white mt-4 p-4 rounded-md'>
                                                {/* Basic Info */}
                                                <h2 className='text-xs lg:text-sm font-bold text-yellow-200'>{practicedSet.practiceTitle}</h2>
                                                <p className='text-xs lg:text-sm text-gray-100'><strong>Date Practiced:</strong> {new Date(practicedSet.datePracticed).toLocaleDateString()}</p>
                                                <p className='text-xs lg:text-sm text-gray-100'><strong>Time Taken:</strong> {convertTimeTaken(practicedSet.timeTaken)}</p>
                                                <p className='text-xs lg:text-sm text-gray-100'><strong>Marks Obtained:</strong> {practicedSet.marksObtained} / {practicedSet.fullMarks}</p>
                                                <p className='text-xs lg:text-sm text-gray-100'><strong>Percentage:</strong> {practicedSet.percentage.toFixed(2)}%</p>
                                                <div className="progress-container mt-4">
                                                    <div className="progress-bar">
                                                        <div className="progress rounded-lg px-4 py-1" style={{ width: `${practicedSet.percentage}%` }}>
                                                            {`${practicedSet.percentage.toFixed(2)}%`}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col text-left ">
                                                    <h3 className="text-lg lg:text-sm font-bold text-yellow-200 mt-4">Answers:</h3>

                                                    {practicedSet.answers.map((answer, index) => (
                                                        <div key={index} className="mt-2 flex flex-col text-left justify-start space-y-1 border-b border-cyan-200 pb-2">
                                                            <p className='text-white text-xs lg:text-sm'>
                                                                <strong>Question# {index + 1}:</strong> {answer.questionTitle}
                                                            </p>
                                                            <p className={`text-xs lg:text-sm ${answer.marksObtained <= 0 ? "text-red-500" : "text-green-400"}`}>
                                                                <strong>My Answer:</strong> {answer.userMCQAnswer}
                                                            </p>
                                                            <p className={`text-xs lg:text-sm ${answer.marksObtained <= 0 ? "text-red-500" : "text-green-400"}`}>
                                                                <strong>Marks Obtained:</strong> {answer.marksObtained}
                                                            </p>
                                                            <p className='text-gray-100 text-xs lg:text-sm'>
                                                                <strong>Marks for Correct Answer:</strong> {answer.correctAnswerPoints}
                                                            </p>
                                                            {answer.isMCQCorrect === 0 && (
                                                                <p className='text-gray-100 text-xs lg:text-sm'>
                                                                    <strong>Marks for Wrong Answer:</strong> {answer.wrongAnswerPoints}
                                                                </p>
                                                            )}
                                                            {answer.remarks && (
                                                                <p className='text-gray-100 text-xs lg:text-sm'>
                                                                    <strong>Remarks:</strong> {answer.remarks}
                                                                </p>
                                                            )}
                                                            {answer.userShortAnswer && (
                                                                <p className='text-gray-100 text-xs lg:text-sm'>
                                                                    <strong>My Short Answer:</strong> {answer.userShortAnswer}
                                                                </p>
                                                            )}
                                                            {/* Confidence Label */}
                                                            <p className="text-xs lg:text-sm mt-2 text-gray-300">
                                                                Confidence:{" "}
                                                                <span
                                                                className={`text-xs lg:text-sm font-semibold ${
                                                                    answer.confidence === "sure"
                                                                    ? "text-green-400"
                                                                    : answer.confidence === "doubtful"
                                                                    ? "text-yellow-400"
                                                                    : "text-orange-400"
                                                                }`}
                                                                >
                                                                {answer.confidence.toUpperCase()}
                                                                </span>
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                                {/* Confidence Breakdown */}
                                                <div className="mt-6 bg-gray-800 p-4 rounded-md">
                                                    <h3 className="text-lg mb-3 text-cyan-300">Confidence Breakdown</h3>
                                                    <div className="space-y-3 text-sm">
                                                        <div>
                                                            <p className="text-green-400 font-medium text-xs lg:text-sm">Sure: {practicedSet.sureStats.total} ({practicedSet.sureStats.percent}%)</p>
                                                            <p className='text-xs lg:text-sm text-gray-100'>✔ {practicedSet.sureStats.correct} | ✖ {practicedSet.sureStats.incorrect}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-yellow-400 font-medium text-xs lg:text-sm">Doubtful: {practicedSet.doubtfulStats.total} ({practicedSet.doubtfulStats.percent}%)</p>
                                                            <p className='text-xs lg:text-sm text-gray-100'>✔ {practicedSet.doubtfulStats.correct} | ✖ {practicedSet.doubtfulStats.incorrect}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-orange-400 font-medium text-xs lg:text-sm">Guess: {practicedSet.guessStats.total} ({practicedSet.guessStats.percent}%)</p>
                                                            <p className='text-xs lg:text-sm text-gray-100'>✔ {practicedSet.guessStats.correct} | ✖ {practicedSet.guessStats.incorrect}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Pie Chart */}
                                                <div className="mt-8 flex flex-col items-center">
                                                    <h3 className="text-cyan-300 mb-4 text-lg font-semibold">Confidence Distribution</h3>
                                                    <div className="relative w-44 h-44">
                                                        <div
                                                            className="w-44 h-44 rounded-full"
                                                            style={{
                                                                background: `conic-gradient(
                                                                    #22c55e 0deg ${practicedSet.sureDeg}deg,
                                                                    #eab308 ${practicedSet.sureDeg}deg ${practicedSet.sureDeg + practicedSet.doubtfulDeg}deg,
                                                                    #f97316 ${practicedSet.sureDeg + practicedSet.doubtfulDeg}deg 360deg
                                                                )`
                                                            }}
                                                        />
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <div className="w-28 h-28 bg-gray-900 rounded-full flex flex-col items-center justify-center">
                                                                <p className="text-xs text-gray-100">Total</p>
                                                                <p className="text-lg font-bold text-gray-200">{practicedSet.answers.length}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Accuracy Bars */}
                                                <div className="mt-8 w-full max-w-md">
                                                    <h3 className="text-cyan-300 mb-3">Confidence Accuracy</h3>
                                                    <div className="space-y-4">
                                                        {[
                                                            { label: "Sure", color: "green", acc: practicedSet.sureAccuracy, stats: practicedSet.sureStats },
                                                            { label: "Doubtful", color: "yellow", acc: practicedSet.doubtfulAccuracy, stats: practicedSet.doubtfulStats },
                                                            { label: "Guess", color: "orange", acc: practicedSet.guessAccuracy, stats: practicedSet.guessStats }
                                                        ].map(item => (
                                                            <div key={item.label}>
                                                                <p className={`text-${item.color}-400`}>{item.label} Accuracy: {item.acc}%</p>
                                                                <div className="w-full bg-gray-700 h-2.5 rounded">
                                                                    <div
                                                                        className={`bg-${item.color}-500 h-2.5 rounded`}
                                                                        style={{ width: `${item.acc}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Confidence Matrix */}
                                                <div className="mt-10">
                                                    <h3 className="text-cyan-300 text-lg mb-4 text-center font-semibold">
                                                        Confidence vs Accuracy Matrix
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-3 max-w-md mx-auto text-center text-sm">
                                                        <div className="bg-green-900/40 border border-green-500 p-4 rounded">
                                                            <p className="text-green-400 font-bold text-lg">{practicedSet.quadrantStats.mastery}</p>
                                                            <p className='text-white'>🎯 Mastery</p>
                                                        </div>
                                                        <div className="bg-red-900/40 border border-red-500 p-4 rounded">
                                                            <p className="text-red-400 font-bold text-lg">{practicedSet.quadrantStats.overconfidence}</p>
                                                            <p className='text-white'>⚠️ Overconfident</p>
                                                        </div>
                                                        <div className="bg-yellow-900/40 border border-yellow-500 p-4 rounded">
                                                            <p className="text-yellow-400 font-bold text-lg">{practicedSet.quadrantStats.underconfidence}</p>
                                                            <p className='text-white'>🤔 Underconfident</p>
                                                        </div>
                                                        <div className="bg-gray-800 border border-gray-500 p-4 rounded">
                                                            <p className="text-gray-300 font-bold text-lg">{practicedSet.quadrantStats.weak}</p>
                                                            <p className='text-white'>📉 Weak Area</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Insights */}
                                                <div className="mt-6 bg-gray-800 p-4 rounded text-sm text-gray-300 max-w-md mx-auto">
                                                    {practicedSet.quadrantStats.overconfidence > practicedSet.quadrantStats.mastery && (
                                                        <p className="text-red-400">⚠️ You are showing signs of overconfidence. Focus on concept clarity.</p>
                                                    )}
                                                    {practicedSet.quadrantStats.underconfidence > 0 && (
                                                        <p className="text-yellow-400">🤔 You knew some answers but lacked confidence. Trust your preparation.</p>
                                                    )}
                                                    {practicedSet.quadrantStats.mastery > practicedSet.quadrantStats.overconfidence && (
                                                        <p className="text-green-400">✅ Strong conceptual clarity and confidence alignment.</p>
                                                    )}
                                                    {practicedSet.quadrantStats.weak > 0 && (
                                                        <p className="text-gray-400">📉 Some topics need revision. Focus on fundamentals.</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>

            <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
                <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
                    <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
                    <div className="flex flex-wrap gap-4 text-sm">
                        <Link to="/about-us" className="hover:text-teal-300">About</Link>
                        <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
                        <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
                        <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PracticeMCQOthers;